import { SettingEnabledLozenge } from 'components/node_settings/SettingEnabledLozenge';
import { BorderlessSettingsSection } from 'components/node_settings/styled';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import { Setting } from 'components/node_settings/V4Settings/Setting';
import { SettingsList } from 'components/node_settings/V4Settings/styled';
import { useUser } from 'contexts/UserContext';
import { V4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import {
    SectionDescription,
    SectionHeading,
    SectionTitle,
} from 'pages/Settings/V4SensorSettings/styled';
import { ReactNode } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled from 'styled-components';
import { isUserInternal } from 'utils/RouteHelper';

const LabelWithInfo = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
`;

function labelWithOptionalPopup(
    label: string,
    description: string,
    useInfoPopup: boolean,
): ReactNode {
    if (!useInfoPopup) {
        return label;
    }
    return (
        <LabelWithInfo>
            {label}
            <InfoPopup title={label}>{description}</InfoPopup>
        </LabelWithInfo>
    );
}

type Props = Readonly<{
    settings: V4SensorSettings;
}>;

export function AdvancedSettingsSection({ settings }: Props) {
    const { user } = useUser();
    const useInfoPopup = isUserInternal(user?.ApplicationRole);
    const { envelopedData } = settings;

    return (
        <BorderlessSettingsSection>
            <SectionHeading>
                <SectionTitle>{V4_SETTINGS_TEXT.sections.advancedSettings}</SectionTitle>
                <SectionDescription>
                    {V4_SETTINGS_TEXT.descriptions.advancedSettingsDescription}
                </SectionDescription>
            </SectionHeading>
            <SettingsList>
                <Setting name={V4_SETTINGS_TEXT.settingNames.vibrationX}>
                    <SettingEnabledLozenge enabled={settings.vibrationX} />
                </Setting>
                <Setting name={V4_SETTINGS_TEXT.settingNames.vibrationY}>
                    <SettingEnabledLozenge enabled={settings.vibrationY} />
                </Setting>
                <Setting name={V4_SETTINGS_TEXT.settingNames.vibrationZ}>
                    <SettingEnabledLozenge enabled={settings.vibrationZ} />
                </Setting>
                <Setting
                    name={labelWithOptionalPopup(
                        V4_SETTINGS_TEXT.settingNames.envelopedData,
                        V4_SETTINGS_TEXT.descriptions.envelopedDataHelper,
                        useInfoPopup,
                    )}
                    helperText={
                        !useInfoPopup && envelopedData.enabled
                            ? V4_SETTINGS_TEXT.descriptions.envelopedDataHelper
                            : undefined
                    }
                >
                    <SettingEnabledLozenge enabled={envelopedData.enabled} />
                </Setting>
                {envelopedData.enabled && (
                    <>
                        <Setting
                            name={V4_SETTINGS_TEXT.settingNames.samplingFrequency}
                            indented
                        >
                            {envelopedData.samplingFrequencyHz} Hz
                        </Setting>
                        <Setting
                            name={V4_SETTINGS_TEXT.settingNames.numberOfSamples}
                            indented
                        >
                            {envelopedData.numberOfSamples} Samples
                        </Setting>
                    </>
                )}
                <Setting
                    name={labelWithOptionalPopup(
                        V4_SETTINGS_TEXT.settingNames.ultrasoundEnveloped,
                        V4_SETTINGS_TEXT.descriptions.ultrasoundHelper,
                        useInfoPopup,
                    )}
                    helperText={
                        !useInfoPopup
                            ? V4_SETTINGS_TEXT.descriptions.ultrasoundHelper
                            : undefined
                    }
                >
                    <SettingEnabledLozenge enabled={settings.ultrasoundEnveloping} />
                </Setting>
            </SettingsList>
        </BorderlessSettingsSection>
    );
}
